import React, { useMemo } from 'react';
import { translate } from 'components/Translation';
import { BodyMBold, BodyS } from 'components/Typography/Typography';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { MAX_ADMIN_COUNT } from 'utils/const/users';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { UserType } from 'views/Users/UsersList';

interface IEditUserDialogProps {
	isDialogOpen: boolean;
	hideDialog: () => void;
	resetEdit: () => void;
	handleEditRequest: () => void;
	isUserTypeChanged: boolean;
	userType: UserType;
	previousUserType: string;
	showAddPermissionsDialog: () => void;
	accounts: SingleCorpAccount[];
	departments: SingleDepartment[];
}

const EditUserDialog = ({
	isDialogOpen,
	hideDialog,
	resetEdit,
	handleEditRequest,
	isUserTypeChanged,
	userType,
	previousUserType,
	showAddPermissionsDialog,
	accounts,
	departments,
}: IEditUserDialogProps) => {
	const warningPath = {
		ENDUSER: {
			MANAGER: 'manager',
			ADMIN: 'admin',
		},
		MANAGER: {
			ADMIN: 'admin',
			ENDUSER: 'toEndUser',
		},
		ADMIN: {
			ENDUSER: 'toEndUser',
			MANAGER: 'toManager',
		},
	};

	const onCancel = () => {
		hideDialog();
		resetEdit();
	};

	const changeUserType = () => {
		if (
			previousUserType !== UserType.ENDUSER ||
			(userType === UserType.ADMIN && accountsWithEmptyAdminSpot.length <= 0) ||
			(userType === UserType.MANAGER && departments.length <= 0)
		) {
			handleEditRequest();
		} else {
			showAddPermissionsDialog();
		}
	};

	const accountsWithEmptyAdminSpot = useMemo(() => {
		return accounts.filter((account) => account.adminsCount < MAX_ADMIN_COUNT);
	}, [accounts]);

	return (
		<ConditionalRender
			show={isUserTypeChanged}
			onTrue={() => (
				<ConfirmationDialog
					isOpen={isDialogOpen}
					hideDialog={hideDialog}
					buttons={[
						{
							text: translate('cancel'),
							onClick: onCancel,
							color: 'tertiary-light',
						},
						{
							text: translate('change'),
							onClick: changeUserType,
							color: 'primary-light',
						},
					]}
					header={translate('users.details.changeUserTypeHeader')}
					dialogBody={
						<>
							<BodyS color="secondary-light">{translate('users.edit.changeUserSubheader')}</BodyS>
							<BodyMBold color="danger">
								{translate(
									`users.details.changeUserTypeWarning.${warningPath[previousUserType][userType]}`
								)}
							</BodyMBold>
							<ConditionalRender
								show={userType !== UserType.ENDUSER}
								onTrue={() => (
									<BodyMBold color="danger">
										{translate('users.details.changeUserTypeWarning.addAuthorities')}
									</BodyMBold>
								)}
							/>
						</>
					}
				/>
			)}
			onFalse={() => (
				<ConfirmationDialog
					isOpen={isDialogOpen}
					hideDialog={hideDialog}
					buttons={[
						{
							text: translate('cancel'),
							onClick: onCancel,
							color: 'tertiary-light',
						},
						{
							text: translate('change'),
							onClick: handleEditRequest,
							color: 'primary-light',
						},
					]}
					header={translate('users.edit.changeUserHeader')}
					dialogBody={<BodyS color="secondary-light">{translate('users.edit.changeUserSubheader')}</BodyS>}
				/>
			)}
		/>
	);
};

export default EditUserDialog;
